<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import MmDialog from './base/MmDialog.vue'
import MmIcon from './base/MmIcon.vue'
import MmSelectOptions from './MmSelectOptions.vue'
import { mmFieldKey } from './base/field'
import { useMmText } from './base/text'
import { useMmPortalTheme } from './base/portalTheme'
import { enabledOptions, moveOption, matchOption } from './base/select.mjs'
import type { MmSelectOption, MmSelectValue } from './base/select'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{ modelValue?: MmSelectValue; options: MmSelectOption[]; placeholder?: string; label?: string; size?: 36 | 44; disabled?: boolean; error?: string | boolean; id?: string; name?: string; hideSelectedIcon?: boolean; modelModifiers?: { number?: boolean } }>(), { size: 44 })
const emit = defineEmits<{ 'update:modelValue': [value: MmSelectValue]; change: [value: MmSelectValue] }>()
const { t } = useMmText()
const field = inject(mmFieldKey, undefined)
const uid = `mm-select-${useId()}`
const triggerId = computed(() => props.id || field?.id || uid)
const listId = `${uid}-list`
const origin = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const list = ref<HTMLElement | null>(null)
const open = ref(false)
const mobile = ref(false)
const active = ref(-1)
const selected = computed(() => props.options.find(option => option.value === props.modelValue))
const invalid = computed(() => !!(props.error || field?.error.value))
const errorText = computed(() => typeof props.error === 'string' ? props.error : '')
const accessibleLabel = computed(() => props.label || t('mm.select.choose'))
const position = ref<Record<string, string>>({})
const { theme, hue } = useMmPortalTheme(origin, open)
let media: MediaQueryList | undefined
let query = ''
let typedAt = 0
const close = (restore = true) => {
  const wasMobile = mobile.value
  open.value = false
  query = ''
  // The shared mobile dialog owns focus return and its scroll/inert lock.
  if (restore && !wasMobile) void nextTick(() => trigger.value?.focus())
}
const place = () => {
  if (!open.value || mobile.value || !trigger.value) return
  const rect = trigger.value.getBoundingClientRect()
  const width = Math.min(Math.max(rect.width, 232), window.innerWidth - 32)
  const below = window.innerHeight - rect.bottom - 16
  const above = rect.top - 16
  const upward = below < 180 && above > below
  const height = Math.min(320, Math.max(80, upward ? above : below))
  position.value = { left: `${Math.max(16, Math.min(rect.left, window.innerWidth - width - 16))}px`, width: `${width}px`, maxHeight: `${height}px`, ...(upward ? { bottom: `${window.innerHeight - rect.top + 8}px` } : { top: `${rect.bottom + 8}px` }) }
}
const reveal = async () => {
  if (props.disabled || open.value) return
  active.value = props.options.findIndex(option => option.value === props.modelValue && !option.disabled)
  if (active.value < 0) active.value = enabledOptions(props.options)[0] ?? -1
  open.value = true
  await nextTick()
  place()
  // Wait for the dialog's initial close-button focus before moving into its list.
  await nextTick()
  list.value?.focus()
  scrollActive()
}
const scrollActive = () => void nextTick(() => document.getElementById(`${listId}-${active.value}`)?.scrollIntoView({ block: 'nearest' }))
const choose = (index: number) => {
  const option = props.options[index]
  if (!option || option.disabled || props.disabled) return
  if (option.value !== props.modelValue) {
    emit('update:modelValue', option.value)
    emit('change', option.value)
  }
  close()
}
const keyboard = (event: KeyboardEvent) => {
  // Keep page/player shortcuts from interpreting list navigation or typeahead.
  if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'Escape', 'Tab'].includes(event.key) || (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey)) event.stopPropagation()
  if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
    event.preventDefault()
    active.value = moveOption(props.options, active.value, event.key)
    scrollActive()
  } else if (event.key === 'Enter' || (event.key === ' ' && !query)) {
    event.preventDefault(); choose(active.value)
  } else if (event.key === 'Escape') {
    event.preventDefault(); event.stopPropagation(); close()
  } else if (event.key === 'Tab' && mobile.value) {
    event.preventDefault()
    event.stopPropagation()
    list.value?.closest('[role="dialog"]')?.querySelector<HTMLButtonElement>('.dialog-head button')?.focus()
  } else if (event.key === 'Tab' && !mobile.value) {
    event.preventDefault()
    const controls = [...document.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled),textarea:not(:disabled),[tabindex="0"]')].filter(e => e.getClientRects().length && !e.closest('[hidden],[inert]') && !list.value?.contains(e))
    const index = controls.indexOf(trigger.value!)
    close(false)
    void nextTick(() => (controls[index + (event.shiftKey ? -1 : 1)] || trigger.value)?.focus())
  } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
    event.preventDefault()
    const now = Date.now()
    query = now - typedAt > 600 ? event.key : query + event.key
    typedAt = now
    const repeated = [...query].every(char => char === query[0])
    active.value = matchOption(props.options, repeated ? query[0] : query, active.value)
    scrollActive()
  }
}
const triggerKey = async (event: KeyboardEvent) => {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  await reveal()
  if (event.key !== 'ArrowDown') { active.value = moveOption(props.options, active.value, event.key === 'ArrowUp' ? 'End' : event.key); scrollActive() }
}
const outside = (event: PointerEvent) => {
  if (!mobile.value && open.value && !origin.value?.contains(event.target as Node) && !list.value?.contains(event.target as Node)) close(false)
}
const breakpoint = () => { if (open.value) close(); mobile.value = media?.matches || false }
watch(() => [props.disabled, props.options] as const, () => {
  if (props.disabled) close()
  else if (open.value && (active.value < 0 || !props.options[active.value] || props.options[active.value].disabled)) active.value = enabledOptions(props.options)[0] ?? -1
}, { deep: true })
onMounted(() => {
  media = window.matchMedia('(max-width: 767px)'); breakpoint()
  media.addEventListener('change', breakpoint)
  document.addEventListener('pointerdown', outside)
  window.addEventListener('resize', place)
  window.addEventListener('scroll', place, true)
})
onBeforeUnmount(() => {
  media?.removeEventListener('change', breakpoint)
  document.removeEventListener('pointerdown', outside)
  window.removeEventListener('resize', place)
  window.removeEventListener('scroll', place, true)
})
</script>

<template>
  <span ref="origin" class="mm-select mm-root">
    <button v-bind="$attrs" :id="triggerId" ref="trigger" type="button" class="input mm-select-trigger" :class="{ invalid, 'mm-select-small': size === 36, placeholder: !selected }" :disabled="disabled" :aria-label="`${accessibleLabel}: ${selected?.label || placeholder || t('mm.select.choose')}`" aria-haspopup="listbox" :aria-expanded="open" :aria-controls="listId" :aria-invalid="invalid || undefined" :aria-describedby="[field?.describedBy.value, errorText && `${uid}-error`].filter(Boolean).join(' ') || undefined" @click="open ? close() : reveal()" @keydown="triggerKey">
      <MmIcon v-if="selected?.icon" :name="selected.icon" :size="20" /><span>{{ selected?.label || placeholder || t('mm.select.choose') }}</span><MmIcon name="down" :size="20" />
    </button>
    <input v-if="name" type="hidden" :name="name" :value="modelValue" :disabled="disabled">
    <span v-if="errorText" :id="`${uid}-error`" class="error-text" role="alert">{{ errorText }}</span>
    <!-- Closed options remain in SSR HTML, including when this becomes a mobile sheet. -->
    <Teleport to="body" :disabled="!open || mobile">
      <div v-show="open && !mobile" class="mm-root mm-overlay-root" :data-theme="theme" :style="{ '--live-h': hue }">
        <div v-if="!mobile" :id="listId" ref="list" class="mm-select-list" :style="position" role="listbox" :aria-label="accessibleLabel" :aria-activedescendant="active >= 0 ? `${listId}-${active}` : undefined" tabindex="0" @keydown="keyboard">
          <MmSelectOptions :id="listId" :options="options" :value="modelValue" :active="active" :hide-selected-icon="hideSelectedIcon" @activate="active = $event" @choose="choose" />
        </div>
      </div>
    </Teleport>
    <MmDialog v-if="mobile" :model-value="open" :title="accessibleLabel" @close="close">
      <div :id="listId" ref="list" class="mm-select-mobile-list" role="listbox" :aria-label="accessibleLabel" :aria-activedescendant="active >= 0 ? `${listId}-${active}` : undefined" tabindex="0" @keydown="keyboard"><MmSelectOptions :id="listId" :options="options" :value="modelValue" :active="active" :hide-selected-icon="hideSelectedIcon" @activate="active = $event" @choose="choose" /></div>
    </MmDialog>
  </span>
</template>

<style scoped>
.mm-select { display: grid; gap: var(--s-2); min-width: 0; width: 100%; }
.mm-select .mm-select-trigger { display: flex; align-items: center; gap: var(--s-2); width: 100%; min-height: 44px; padding: var(--s-2) var(--s-3); color: var(--text); font: 400 var(--t-body)/1.4 var(--font-ui); text-align: start; cursor: pointer; }
.mm-select-trigger > span { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.mm-select-trigger.placeholder { color: var(--text-3); }
.mm-select-trigger:disabled { opacity: .45; cursor: not-allowed; }
.mm-select .mm-select-small { min-height: 36px; padding-block: var(--s-1); }
.mm-select-list { position: fixed; z-index: var(--z-menu); overflow-y: auto; overscroll-behavior: contain; padding: var(--s-1); border: 1px solid var(--line-strong); border-radius: var(--r-md); background: var(--glass); box-shadow: var(--e-2); backdrop-filter: blur(24px); animation: mm-pop-in var(--d-base) var(--ease-out); }
.mm-select-list:focus-visible, .mm-select-mobile-list:focus-visible { outline: 2px solid var(--focus); outline-offset: -2px; }
.mm-select-mobile-list { min-height: 0; max-height: 60dvh; overflow-y: auto; overscroll-behavior: contain; padding: var(--s-1); border-radius: var(--r-sm); }
@media (max-width: 767px), (any-pointer: coarse) { .mm-select .mm-select-small { min-height: 44px; } }
@media (prefers-reduced-motion: reduce) { .mm-select-list { animation: none; } }
</style>
